const TOKENS_RegExp =
  /(\[[^[]*\])|(\\)?([Hh]mm(ss)?|Mo|MM?M?M?|Do|DDDo|DD?D?D?|ddd?d?|do?|w[o|w]?|W[o|W]?|Qo?|N{1,5}|YYYYYY|YYYYY|YYYY|YY|y{2,4}|yo?|gg(ggg?)?|GG(GGG?)?|e|E|a|A|hh?|HH?|kk?|mm?|ss?|S{1,9}|x|X|zz?|ZZ?|.)/g;

const TOKENS = ["D", "DD", "Do", "M", "MM", "MMM", "MMMM", "Y", "YY", "YYYY"] as const;
type TOKEN = typeof TOKENS[number];

export const DateTime = (dateTime: string = new Date().toISOString()) => {
  const format = (dateFormat: string) => {
    if (!isValid()) return "Invalid DateTime";
    const date = new Date(dateTime);

    const tokens = dateFormat.match(TOKENS_RegExp);

    const formattedChunks: string[] = tokens.map((token) => {
      if (!isToken(token)) return token;
      return formatters(date, token as TOKEN);
    });

    return formattedChunks.join("");
  };

  /**
   * Adjusts the end date to include the number of late submissions
   * @param days number of days that late submissions are allowed
   */
  const addDays = (days: number) => {
    if (!isValid()) return "";
    const date = new Date(dateTime);
    date.setDate(date.getDate() + days);
    return date.toISOString();
  };

  const addZero = (num: number) => ("0" + num).slice(-2);

  const formatOrdinal = (day: number) => {
    const ordinal = (suffix: string) => day + suffix;
    if (day > 3 && day < 21) return ordinal("th");
    switch (day % 10) {
      case 1:
        return ordinal("st");
      case 2:
        return ordinal("nd");
      case 3:
        return ordinal("rd");
      default:
        return ordinal("th");
    }
  };

  const formatters = (date: Date, token: TOKEN): string => {
    switch (token) {
      case "D":
        return date.getDate().toString();
      case "DD":
        return addZero(date.getDate());
      case "Do":
        return formatOrdinal(date.getDate());
      case "M":
        return (date.getMonth() + 1).toString();
      case "MM":
        return addZero(date.getMonth() + 1);
      case "MMM":
        return date.toLocaleString(undefined, {
          month: "short",
        });
      case "MMMM":
        return date.toLocaleString(undefined, {
          month: "long",
        });
      case "Y":
        return date.getFullYear().toString();
      case "YY":
        return date.getFullYear().toString().substring(2);
      case "YYYY":
        return date.getFullYear().toString();
      default:
        return token;
    }
  };

  const isBlankDate = () => dateTime === "" || +new Date(dateTime) < +new Date("0001-01-02");

  const isPast = () => isValid() && +new Date(dateTime) < +new Date();

  /** checks that value is a valid token */
  const isToken = (token: unknown): token is TOKEN => {
    return typeof token === "string" ? TOKENS.map<string>((t) => t).includes(token) : false;
  };

  const isValid = (): boolean => {
    const date = new Date(dateTime);
    return date instanceof Date && !isNaN(date.valueOf());
  };

  const startOfDay = () => {
      if (!isValid()) return "";
      const date = new Date(dateTime);
      const start = new Date(date.getFullYear(), date.getMonth(), date.getDate());
      return start.toISOString();
  };

  return { addDays, format, isBlankDate, isPast, isValid, startOfDay };
};
